/**
 * 创建学生弹窗。
 *
 * 关键交互（见方案 1.1）：家长可以不填初始密码，由服务端生成强密码，
 * 该密码**只在创建响应里出现一次**，所以创建成功后必须立刻醒目展示并提示
 * "请告诉孩子，并让他首次登录后修改"。离开这个弹窗就再也拿不到了。
 */
import { useState, type FormEvent } from 'react';
import { GENDERS, GRADES, createStudentSchema, gradeLabel } from '@fht/shared';
import { useCreateStudent } from '../../lib/queries';
import { toErrorMessage } from '../../lib/api/error';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { useToast } from '../ui/Toast';

const GENDER_LABEL: Record<string, string> = { male: '男孩', female: '女孩' };

export interface CreateStudentModalProps {
  open: boolean;
  onClose: () => void;
  /** 创建成功后的回调（父组件用来刷新列表 / 提示）。 */
  onCreated?: (studentId: string) => void;
}

export function CreateStudentModal({ open, onClose, onCreated }: CreateStudentModalProps) {
  const toast = useToast();
  const createStudent = useCreateStudent();

  const [nickname, setNickname] = useState('');
  const [username, setUsername] = useState('');
  const [gender, setGender] = useState<string>(GENDERS[0]);
  const [birthday, setBirthday] = useState('');
  const [gradeCode, setGradeCode] = useState<string>(GRADES[0].code);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /** 创建成功后的结果：初始密码只在这里出现一次。 */
  const [created, setCreated] = useState<{ id: string; nickname: string; password: string | null } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setNickname('');
    setUsername('');
    setGender(GENDERS[0]);
    setBirthday('');
    setGradeCode(GRADES[0].code);
    setErrors({});
    setFormError(null);
    setCreated(null);
    setCopied(false);
  };

  const close = () => {
    resetForm();
    createStudent.reset();
    onClose();
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const parsed = createStudentSchema.safeParse({ nickname, username, gender, birthday, gradeCode });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      setFormError('请检查标红的内容');
      return;
    }

    try {
      const result = await createStudent.mutateAsync(parsed.data);
      setCreated({
        id: result.student?.id ?? '',
        nickname: result.student?.nickname ?? nickname,
        password: result.initialPassword ?? null,
      });
      onCreated?.(result.student?.id ?? '');
    } catch (err) {
      setFormError(toErrorMessage(err));
    }
  };

  const copyPassword = async () => {
    if (!created?.password) return;
    try {
      await globalThis.navigator.clipboard.writeText(created.password);
      setCopied(true);
      globalThis.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.showToast('复制失败，请手动记下密码', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={created ? '孩子账号已创建' : '添加孩子'}
      description={
        created
          ? '请把这串初始密码告诉孩子，并让他首次登录后修改密码。'
          : '孩子的用户名用于登录，建议用拼音或英文'
      }
      size="md"
    >
      {created ? (
        <div className="space-y-4">
          <div className="rounded-2xl bg-emerald-50 p-4 text-center">
            <p className="text-sm text-emerald-800">
              {created.nickname} 的初始密码（只显示这一次）
            </p>
            <p className="mt-2 select-all break-all font-mono text-2xl font-bold tracking-wider text-emerald-900">
              {created.password ?? '（由你设置的初始密码）'}
            </p>
            {created.password && (
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => void copyPassword()}>
                {copied ? '已复制' : '复制密码'}
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            提示：孩子首次登录后必须修改密码；如果忘记了，可以在孩子详情页重置密码。
          </p>
          <div className="flex justify-end">
            <Button onClick={close}>知道了</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <Input
            label="昵称"
            name="nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            required
            hint="孩子看到的名字，如「小明」"
            {...(errors.nickname ? { error: errors.nickname } : {})}
          />

          <Input
            label="登录用户名"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="off"
            hint="3~32 位，字母开头，只能包含字母、数字、下划线、连字符"
            {...(errors.username ? { error: errors.username } : {})}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="性别"
              name="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              {...(errors.gender ? { error: errors.gender } : {})}
            >
              {GENDERS.map((value) => (
                <option key={value} value={value}>
                  {GENDER_LABEL[value] ?? value}
                </option>
              ))}
            </Select>

            <Input
              label="出生日期"
              name="birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              required
              {...(errors.birthday ? { error: errors.birthday } : {})}
            />
          </div>

          <Select
            label="年级"
            name="gradeCode"
            value={gradeCode}
            onChange={(e) => setGradeCode(e.target.value)}
            hint="年级决定孩子端的界面模式：低年级自动使用大按钮儿童模式"
            {...(errors.gradeCode ? { error: errors.gradeCode } : {})}
          >
            {GRADES.map((grade) => (
              <option key={grade.code} value={grade.code}>
                {gradeLabel(grade.code)}
              </option>
            ))}
          </Select>

          {formError && (
            <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
              {formError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={close}>
              取消
            </Button>
            <Button type="submit" loading={createStudent.isPending}>
              创建孩子账号
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
